import React, { useCallback, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Modal, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileLines,
  faCommentDots,
  faFolderOpen,
  faStickyNote,
  faExternalLinkAlt,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';

import { useGetPresignedClaimFile } from '../../../services/claims';
import {
  getWorkspaceRequestContentionRows,
  getWorkspaceToeSummary,
} from './dbqWorkspaceUtils';

const MAX_VISIBLE_EVIDENCE_FILES = 3;

const getProviderStatusLabel = (requestStatus, fallbackLabel) => {
  if (requestStatus === 'sent_to_provider') return 'Ready for Review';
  if (requestStatus === 'provider_in_progress') return 'In Progress';
  if (requestStatus === 'changes_requested') return fallbackLabel || 'Changes Requested';
  return fallbackLabel || 'Draft';
};

const getProviderStatusVariant = (requestStatus, fallbackVariant) => {
  if (requestStatus === 'sent_to_provider') return 'primary';
  if (requestStatus === 'provider_in_progress') return 'info';
  if (requestStatus === 'changes_requested') return 'warning';
  return fallbackVariant || 'gray';
};

const DbqProviderWorkQueueCard = ({
  userDbq,
  toeOptionsById,
  onOpenEditor,
  onPreviewPdf,
  isPreviewingPdf = false,
}) => {
  const [showEvidenceModal, setShowEvidenceModal] = useState(false);
  const [openingEvidenceFileId, setOpeningEvidenceFileId] = useState(null);
  const request = userDbq?.request || {};
  const selectedEvidenceFiles = Array.isArray(request.selectedEvidenceFiles)
    ? request.selectedEvidenceFiles
    : [];
  const visibleEvidenceFiles = selectedEvidenceFiles.slice(0, MAX_VISIBLE_EVIDENCE_FILES);
  const suggestionCount = userDbq?.reviewCounts?.pendingSuggestionCount || 0;
  const totalSuggestionCount = userDbq?.reviewCounts?.suggestionCount || 0;
  const commentCount = userDbq?.reviewCounts?.commentCount || 0;
  const claimedConditions = useMemo(() => {
    if (Array.isArray(userDbq?.conditionLabels) && userDbq.conditionLabels.length > 0) {
      return userDbq.conditionLabels.join(', ');
    }
    return userDbq?.conditionTitle || 'No claimed conditions';
  }, [userDbq]);

  const statusLabel = getProviderStatusLabel(request.status, userDbq?.statusMeta?.label);
  const statusVariant = getProviderStatusVariant(request.status, userDbq?.statusMeta?.variant);

  const openEvidenceFile = useGetPresignedClaimFile((res) => {
    window.open(res.url, '_blank');
  });

  const handleOpenEvidenceFile = useCallback(
    (file) => {
      if (!userDbq?.claimId || !file?.fileId) return;

      setOpeningEvidenceFileId(file.fileId);
      openEvidenceFile.mutate(
        { claimId: userDbq.claimId, fileId: file.fileId },
        {
          onSettled: () => {
            setOpeningEvidenceFileId((currentFileId) =>
              currentFileId === file.fileId ? null : currentFileId,
            );
          },
        },
      );
    },
    [openEvidenceFile, userDbq?.claimId],
  );

  const requestContentionRows = useMemo(
    () => getWorkspaceRequestContentionRows(userDbq, toeOptionsById || new Map()),
    [toeOptionsById, userDbq],
  );
  const toeSummary = useMemo(
    () => getWorkspaceToeSummary(userDbq, toeOptionsById || new Map()),
    [toeOptionsById, userDbq],
  );

  return (
    <>
      <Card
        className="border-0 overflow-hidden"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06)' }}
      >
        {/* Status accent bar */}
        <div
          style={{
            height: 3,
            background:
              statusVariant === 'primary'
                ? 'var(--bs-primary)'
                : statusVariant === 'warning'
                  ? 'var(--bs-warning)'
                  : statusVariant === 'info'
                    ? 'var(--bs-info)'
                    : statusVariant === 'gray'
                      ? '#9ca3af'
                    : '#dee2e6',
          }}
        />

        <Card.Body className="p-4">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
            <div className="flex-grow-1">
              <div className="d-flex align-items-center gap-2 mb-1">
                <Badge
                  bg={statusVariant === 'gray' ? 'light' : statusVariant}
                  text={statusVariant === 'gray' ? 'dark' : undefined}
                  className="rounded-pill px-2 py-1"
                  style={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.02em' }}
                >
                  {statusLabel}
                </Badge>
              </div>
              <h5 className="mb-1 fw-semibold" style={{ fontSize: '1.1rem' }}>
                {claimedConditions}
              </h5>
              <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                <FontAwesomeIcon icon={faFileLines} className="me-1" style={{ opacity: 0.6 }} />
                {userDbq?.title || 'Untitled DBQ'}
                {toeSummary && toeSummary !== 'Not selected' ? (
                  <span className="ms-3 text-muted">
                    TOEs: {toeSummary}
                  </span>
                ) : null}
              </div>
              {requestContentionRows.length > 0 ? (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {requestContentionRows.map((requestContention) => (
                    <span
                      key={`${userDbq?.userDbqId}-${requestContention.contention}`}
                      className="px-2 py-1 rounded-pill"
                      style={{
                        fontSize: '0.72rem',
                        background: '#f8fafc',
                        color: '#475569',
                        border: '1px solid #e2e8f0',
                      }}
                    >
                      {requestContention.contentionLabel}
                      {requestContention.toeLabel && requestContention.toeLabel !== 'Not selected'
                        ? ` • ${requestContention.toeLabel}`
                        : ''}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="d-flex flex-shrink-0 gap-2 align-items-start">
              {onPreviewPdf ? (
                <Button
                  variant="outline-secondary"
                  onClick={() => onPreviewPdf?.(userDbq)}
                  disabled={isPreviewingPdf}
                >
                  <FontAwesomeIcon
                    icon={isPreviewingPdf ? faSpinner : faFileLines}
                    spin={isPreviewingPdf}
                    className="me-2"
                  />
                  {isPreviewingPdf ? 'Previewing...' : 'Preview DBQ PDF'}
                </Button>
              ) : null}
              <Button
                variant="primary"
                onClick={() => onOpenEditor?.(userDbq)}
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                Open DBQ
              </Button>
            </div>
          </div>

          {/* Content sections */}
          <Row className="g-3 mt-1">
            {/* Evidence */}
            <Col md={6} lg={4}>
              <div
                className="h-100 rounded-3 p-3"
                style={{ background: '#f8f9fb', border: '1px solid #eef0f4' }}
              >
                <div className="d-flex align-items-center gap-2 mb-2">
                  <FontAwesomeIcon
                    icon={faFolderOpen}
                    style={{ color: 'var(--bs-primary)', opacity: 0.7, fontSize: '0.85rem' }}
                  />
                  <span className="fw-semibold" style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    EVIDENCE
                  </span>
                  {selectedEvidenceFiles.length > 0 ? (
                    <Badge
                      bg="light"
                      text="dark"
                      className="ms-auto rounded-pill"
                      style={{ fontSize: '0.7rem' }}
                    >
                      {selectedEvidenceFiles.length} file{selectedEvidenceFiles.length !== 1 ? 's' : ''}
                    </Badge>
                  ) : null}
                </div>
                {visibleEvidenceFiles.length > 0 ? (
                  <div className="d-flex flex-column gap-1">
                    {visibleEvidenceFiles.map((file) => (
                      <Button
                        key={file.fileId}
                        variant="link"
                        className="p-0 text-start text-decoration-none d-flex align-items-center gap-2"
                        style={{ fontSize: '0.82rem', color: '#475569' }}
                        onClick={() => handleOpenEvidenceFile(file)}
                        disabled={openingEvidenceFileId === file.fileId}
                      >
                        <FontAwesomeIcon
                          icon={faFileLines}
                          style={{ opacity: 0.4, fontSize: '0.75rem', flexShrink: 0 }}
                        />
                        <span className="text-truncate">{file.fileName}</span>
                      </Button>
                    ))}
                    {selectedEvidenceFiles.length > visibleEvidenceFiles.length ? (
                      <Button
                        variant="link"
                        className="p-0 text-decoration-none text-start"
                        style={{ fontSize: '0.8rem' }}
                        onClick={() => setShowEvidenceModal(true)}
                      >
                        +{selectedEvidenceFiles.length - visibleEvidenceFiles.length} more
                      </Button>
                    ) : null}
                  </div>
                ) : (
                  <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                    No evidence packaged.
                  </div>
                )}
              </div>
            </Col>

            {/* Notes */}
            <Col md={6} lg={4}>
              <div
                className="h-100 rounded-3 p-3"
                style={{ background: '#f8f9fb', border: '1px solid #eef0f4' }}
              >
                <div className="d-flex align-items-center gap-2 mb-2">
                  <FontAwesomeIcon
                    icon={faStickyNote}
                    style={{ color: '#f59e0b', opacity: 0.8, fontSize: '0.85rem' }}
                  />
                  <span className="fw-semibold" style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    NOTES
                  </span>
                </div>
                {request.requestNotes ? (
                  <p
                    className="mb-0"
                    style={{
                      fontSize: '0.85rem',
                      color: '#334155',
                      lineHeight: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      whiteSpace: 'break-spaces',
                    }}
                  >
                    {request.requestNotes}
                  </p>
                ) : (
                  <p className="text-muted mb-0" style={{ fontSize: '0.82rem' }}>
                    No notes included.
                  </p>
                )}
              </div>
            </Col>

            {/* Review Summary - combined suggestions + comments */}
            <Col md={12} lg={4}>
              <div
                className="h-100 rounded-3 p-3"
                style={{ background: '#f8f9fb', border: '1px solid #eef0f4' }}
              >
                <div className="d-flex align-items-center gap-2 mb-2">
                  <FontAwesomeIcon
                    icon={faCommentDots}
                    style={{ color: '#6366f1', opacity: 0.7, fontSize: '0.85rem' }}
                  />
                  <span className="fw-semibold" style={{ fontSize: '0.78rem', color: '#64748b' }}>
                    REVIEW ITEMS
                  </span>
                </div>
                <div className="d-flex gap-3">
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 32,
                        height: 32,
                        background: suggestionCount > 0 ? '#eef2ff' : '#f1f5f9',
                        color: suggestionCount > 0 ? '#6366f1' : '#94a3b8',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                      }}
                    >
                      {suggestionCount}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.78rem', fontWeight: 500, color: '#334155' }}>
                        Suggestion{suggestionCount !== 1 ? 's' : ''}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                        {suggestionCount > 0
                          ? 'Pending review'
                          : totalSuggestionCount > 0
                            ? 'All resolved'
                            : 'None'}
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 32,
                        height: 32,
                        background: commentCount > 0 ? '#f0f9ff' : '#f1f5f9',
                        color: commentCount > 0 ? '#0ea5e9' : '#94a3b8',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                      }}
                    >
                      {commentCount}
                    </div>
                    <div>
                      <div style={{ fontSize: '0.78rem', fontWeight: 500, color: '#334155' }}>
                        Comment{commentCount !== 1 ? 's' : ''}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                        {commentCount > 0 ? 'In DBQ form' : 'None'}
                      </div>
                    </div>
                  </div>
                </div>
                {(suggestionCount > 0 || commentCount > 0) ? (
                  <div
                    className="mt-2 text-muted"
                    style={{ fontSize: '0.75rem' }}
                  >
                    {totalSuggestionCount > suggestionCount && totalSuggestionCount > 0
                      ? (
                        suggestionCount > 0
                          ? `${suggestionCount} of ${totalSuggestionCount} suggestion${totalSuggestionCount === 1 ? '' : 's'} remaining. `
                          : `All ${totalSuggestionCount} suggestion${totalSuggestionCount === 1 ? '' : 's'} have been resolved. `
                      )
                      : ''}
                    Review items inline in the DBQ form.
                  </div>
                ) : null}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Evidence modal */}
      <Modal show={showEvidenceModal} onHide={() => setShowEvidenceModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>
            Packaged Evidence
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-muted small mb-3">{claimedConditions}</div>
          {selectedEvidenceFiles.length > 0 ? (
            <div className="d-flex flex-column gap-2">
              {selectedEvidenceFiles.map((file) => (
                <div key={file.fileId} className="d-flex align-items-center gap-2">
                  <FontAwesomeIcon
                    icon={faFileLines}
                    style={{ opacity: 0.4, fontSize: '0.8rem', flexShrink: 0 }}
                  />
                  <Button
                    variant="link"
                    className="p-0 text-decoration-none text-start"
                    style={{ fontSize: '0.85rem', color: '#475569' }}
                    onClick={() => handleOpenEvidenceFile(file)}
                    disabled={openingEvidenceFileId === file.fileId}
                  >
                    {file.fileName}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted">No evidence packaged for this request.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEvidenceModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DbqProviderWorkQueueCard;
